import Image from 'next/image';
import React, { CSSProperties } from 'react';

import { REBASS_TO_TAILWIND_BG_MAP } from '@osmosis/tailwind-ui/styles/mapping';
import { Flex, Box } from '@osmosis/tailwind-ui/components/Box';
import CTAs from '../block-ui/CTAs';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import Media from '../block-ui/Media';
import RichText from '../block-ui/RichText';
import { HeroBannerBlockProps } from '../../../types/composer';
import { heroBannerSchema } from '../../../lib/composer/schemas/heroBanner';

type Props = HeroBannerBlockProps;

const Character = (props: { style: CSSProperties; src: string }) => (
  <Image
    width={140}
    height={50}
    style={{ position: 'absolute', opacity: 0.25, ...(props.style || {}) }}
    src={props.src}
    alt=""
    aria-hidden
  />
);

type BubbleProps = {
  classNames: string;
};

const Bubble = ({ classNames }: BubbleProps) => (
  <Box
    aria-hidden
    className={`h-[50px] w-[50px] absolute opacity-[0.1] rounded-[100%] bg-white ${
      classNames || ''
    }`}
  />
);

const PrimaryBackground = () => {
  return (
    <Box
      className={`absolute top-0 left-0 w-[100%] h-[100%] overflow-hidden z-1 pointer-events-none`}
      aria-hidden
    >
      <Character
        style={{ width: 140, left: 30, bottom: -66 }}
        src="https://d16qt3wv6xm098.cloudfront.net/o-jkp2UzRCa9lTujJUsIQ_TBRwemVU5h/_.png"
      />

      <Character
        style={{ width: 90, right: 50, top: 30 }}
        src="https://d16qt3wv6xm098.cloudfront.net/-Og8NH9bSOWii30_Hvc3wm7ZSbWBwsU_/_.png"
      />

      <Bubble classNames="h-[200px] w-[200px] -left-[100px] -top-[100px]" />

      <Bubble classNames="h-[40px] w-[40px] left-[90px] top-[60px]" />
      <Bubble classNames="h-[240px] w-[240px] -right-[100px] -bottom-[100px]" />

      <Bubble classNames="h-[20px] w-[20px] right-[160px] top-[80px]" />
    </Box>
  );
};

/**
 * Hero banner block.
 */
const HeroBanner = (props: Props) => {
  const {
    heading,
    subheading,
    cta,
    text,
    theme = 'bg-0',
    media,
    anchor,
    topHeader,
    backgroundImage,
  } = props;
  const hasMedia = !!media?.src;

  const bgClasses = REBASS_TO_TAILWIND_BG_MAP[theme] || 'bg-transparent';
  const bgStyles = backgroundImage?.image
    ? {
        backgroundImage: `url(${backgroundImage.image})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      }
    : {};

  return (
    <Box id={anchor} style={bgStyles} className={`relative ${bgClasses}`}>
      {(theme === 'primary' || theme === 'blue' || theme === 'primaryText') && (
        <PrimaryBackground />
      )}

      <Flex
        className={`m-auto py-[24px] md:py-[64px] px-[10px] max-w-none xl:max-w-[1400px] min-h-[590px]`}
      >
        <Flex className="flex-col flex-1 justify-center items-center">
          <Flex className={`flex-col md:flex-row items-center mb-4`}>
            <Flex
              className={`flex-col flex-1 justify-center items-center ${
                hasMedia ? 'items-start' : 'gap-6'
              }`}
            >
              {heading && (
                <Heading
                  text={heading}
                  theme={theme}
                  topHeader={topHeader}
                  textProps={`text-3xl sm:text-4xl md:text-5xl/[53px] ${
                    hasMedia ? 'max-w-none' : 'max-w-[612px]'
                  } mb-2 ${hasMedia ? 'text-left' : 'text-center'}
                `}
                />
              )}
              {subheading && (
                <Subheading
                  text={subheading}
                  theme={theme}
                  textProps={`text-lg md:text-xl font-semibold ${
                    hasMedia ? 'text-left' : 'text-center'
                  }
                `}
                  topHeader={topHeader}
                />
              )}
              {text && (
                <Box className="text-base sm:text-2xl">
                  <RichText
                    alignment={hasMedia ? 'left' : 'center'}
                    theme={theme}
                    html={text}
                    containerProps={{
                      className: `!max-w-[882px]`,
                    }}
                  />
                </Box>
              )}
              {cta?.caption ? (
                <CTAs
                  ctas={[cta]}
                  theme={theme}
                  containerCN={`mt-6 ${
                    hasMedia ? 'justify-start' : 'justify-center'
                  }`}
                  buttonCN="my-[7px] mr-0 font-normal"
                  imageCN="mr-[22px]"
                />
              ) : null}
            </Flex>

            {hasMedia && (
              <Box className={`flex-1`}>
                <Media media={media} />
              </Box>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

HeroBanner.schema = heroBannerSchema;

export default HeroBanner;
